/**
 * AskTeacherSheet – bottom sheet for free-form Q&A with the AI teacher.
 *
 * Uses `/api/teacher/ask` which returns a structured response:
 *   answer_target + respelling + IPA + bilingual example + tip.
 *
 * Pre-filled prompt templates are shown as chips ("How do you say…?",
 * "What does X mean?", "Say it slower", "Give me an example") so the learner
 * can ask with a single tap — the chip expands the template into the input.
 *
 * Props:
 *   - open, onClose, sessionId, practiceDirection
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Question, SpinnerGap, PaperPlaneRight, SpeakerHigh,
} from '@phosphor-icons/react';
import { http, API, apiErrorMessage, getStoredToken } from '@/lib/api';

const QUICK_CHIPS = [
  { label: 'How do you say…', tpl: 'How do you say "____" in the target language?' },
  { label: 'What does this mean?', tpl: 'What does "____" mean?' },
  { label: 'Give me an example', tpl: 'Give me an example sentence using "____".' },
  { label: 'Say it slower', tpl: 'Can you say your last sentence slower and explain each word?' },
];

export default function AskTeacherSheet({ open, onClose, sessionId, practiceDirection }) {
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [ans, setAns] = useState(null);
  const [error, setError] = useState('');
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!open) { setQ(''); setAns(null); setError(''); }
  }, [open]);

  async function ask() {
    if (!q.trim()) return;
    setLoading(true); setError(''); setAns(null);
    try {
      const { data } = await http.post('/teacher/ask', {
        session_id: sessionId,
        question: q.trim(),
        practice_direction: practiceDirection,
      });
      setAns(data);
    } catch (e) { setError(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }

  async function speak(text) {
    if (!text) return;
    setPlaying(true);
    try {
      const resp = await fetch(`${API}/tts`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(getStoredToken() ? { Authorization: `Bearer ${getStoredToken()}` } : {}),
        },
        body: JSON.stringify({ text, speed: 0.85 }),
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => { URL.revokeObjectURL(url); setPlaying(false); };
      audio.onerror = () => { URL.revokeObjectURL(url); setPlaying(false); };
      audio.play().catch(() => setPlaying(false));
    } catch { setPlaying(false); }
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/40"
            data-testid="ask-teacher-backdrop"
          />
          <motion.div
            key="sheet"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
            className="fixed inset-x-0 bottom-0 z-50 max-h-[82vh] overflow-y-auto rounded-t-3xl bg-[var(--lf-surface)] p-5 shadow-2xl"
            data-testid="ask-teacher-sheet"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Question size={18} weight="duotone" className="text-[var(--lf-primary)]" />
                <div className="font-bold">Ask the teacher</div>
              </div>
              <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full bg-[var(--lf-surface-muted)]" data-testid="ask-teacher-close" aria-label="Close ask teacher panel">
                <X size={14} aria-hidden="true" />
              </button>
            </div>

            <div className="mt-3 flex flex-wrap gap-2" data-testid="ask-teacher-chips">
              {QUICK_CHIPS.map((c) => (
                <button
                  key={c.label}
                  onClick={() => setQ(c.tpl)}
                  className="rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)] px-3 py-1.5 text-xs font-semibold hover:bg-[var(--lf-surface-muted)]"
                  data-testid={`ask-chip-${c.label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z-]/g, '')}`}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <div className="mt-3 flex gap-2">
              <textarea
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Ask anything — English, Spanish, or mixed."
                rows={2}
                className="lf-input flex-1 text-sm"
                data-testid="ask-teacher-input"
              />
              <button
                onClick={ask}
                disabled={loading || !q.trim()}
                className="lf-btn lf-btn-primary px-4"
                data-testid="ask-teacher-submit"
              >
                {loading ? <SpinnerGap size={14} className="animate-spin" /> : <PaperPlaneRight size={14} weight="fill" />}
              </button>
            </div>

            {error ? <div className="mt-2 text-sm text-[var(--lf-destructive)]">{error}</div> : null}

            {ans ? (
              <div className="mt-4 rounded-2xl border border-[var(--lf-border)] bg-[var(--lf-surface-muted)] p-4" data-testid="ask-teacher-answer">
                <div className="text-lg font-bold" data-testid="ask-teacher-answer-target">{ans.answer_target}</div>
                {ans.respelling ? (
                  <div className="text-xs text-[var(--lf-text-muted)] font-mono mt-0.5">
                    {ans.respelling} {ans.ipa ? <span className="ml-2">/{ans.ipa}/</span> : null}
                  </div>
                ) : null}
                <div className="mt-2 text-sm">{ans.answer_native}</div>
                {ans.example_target ? (
                  <div className="mt-3 rounded-xl bg-white p-3">
                    <div className="italic text-sm">“{ans.example_target}”</div>
                    {ans.example_native ? <div className="mt-0.5 text-xs text-[var(--lf-text-muted)]">{ans.example_native}</div> : null}
                  </div>
                ) : null}
                {ans.tip ? <div className="mt-2 text-xs italic text-[var(--lf-text-muted)]">💡 {ans.tip}</div> : null}
                <button
                  onClick={() => speak(ans.answer_target)}
                  disabled={playing}
                  className="lf-btn lf-btn-secondary mt-3 w-full py-2 text-sm"
                  data-testid="ask-teacher-hear-btn"
                >
                  {playing ? <SpinnerGap size={14} className="animate-spin" /> : <SpeakerHigh size={14} weight="fill" />}
                  Hear it
                </button>
              </div>
            ) : null}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
